/**
 * useExecutionSignals Hook
 *
 * Manages execution signal state for the Exo Panel.
 * ISSUE-224 — `getExecutionSignalService()` (main API by default; mock when
 * `REACT_APP_EXO_EXECUTION_SIGNALS_MOCK=true`).
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ExecutionSignal,
  QueueStatus,
  ExoPanelStatus,
  BlockReason,
} from '../../types/executionSignal';
import { getExecutionSignalService } from '../../services/executionSignalService';

interface UseExecutionSignalsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseExecutionSignalsReturn {
  // Current state
  currentSignal: ExecutionSignal | null;
  pendingSignals: ExecutionSignal[];
  queueStatus: QueueStatus;
  panelStatus: ExoPanelStatus;
  
  // Loading states
  isLoading: boolean;
  isResolving: boolean;
  error: string | null;
  
  // Actions
  refreshSignals: () => Promise<void>;
  completeSignal: (signalId: string, notes?: string) => Promise<void>;
  blockSignal: (signalId: string, reason: BlockReason, notes?: string) => Promise<void>;
  escalateSignal: (signalId: string, reason: string, toRole?: string) => Promise<void>;
  requestClarification: (signalId: string, question: string) => Promise<void>;
  acknowledgeSignal: (signalId: string) => void;
}

const DEFAULT_QUEUE_STATUS: QueueStatus = {
  total_pending: 0,
  high_priority: 0,
  blocked_count: 0
};

const DEFAULT_PANEL_STATUS: ExoPanelStatus = {
  status: 'system_stable',
  message: 'System stable.',
  waiting_on: []
};

// eslint-disable-next-line max-lines-per-function -- single hook for panel state, refresh, and user actions
export function useExecutionSignals(
  options: UseExecutionSignalsOptions = {}
): UseExecutionSignalsReturn {
  const { autoRefresh = false, refreshInterval = 30000 } = options;
  const signalService = useMemo(() => getExecutionSignalService(), []);

  // State
  const [currentSignal, setCurrentSignal] = useState<ExecutionSignal | null>(null);
  const [pendingSignals, setPendingSignals] = useState<ExecutionSignal[]>([]);
  const [queueStatus, setQueueStatus] = useState<QueueStatus>(DEFAULT_QUEUE_STATUS);
  const [panelStatus, setPanelStatus] = useState<ExoPanelStatus>(DEFAULT_PANEL_STATUS);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refresh all signal data
  const refreshSignals = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get next signal and queue status
      const nextResponse = await signalService.getNextSignal();
      setCurrentSignal(nextResponse.signal);
      setQueueStatus(nextResponse.queue_status);

      // Get all pending signals
      const pending = await signalService.getPendingSignals();
      setPendingSignals(pending);

      // Get overall status
      const status = await signalService.getStatus();
      setPanelStatus(status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load signals');
      console.error('[useExecutionSignals] Error refreshing signals:', err);
    } finally {
      setIsLoading(false);
    }
  }, [signalService]);

  // Complete a signal
  const completeSignal = useCallback(async (signalId: string, notes?: string) => {
    setIsResolving(true);
    setError(null);

    try {
      await signalService.resolveSignal(signalId, {
        resolution_type: 'completed',
        notes
      });
      
      // Refresh to get next signal
      await refreshSignals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete signal');
      throw err;
    } finally {
      setIsResolving(false);
    }
  }, [refreshSignals, signalService]);

  // Block a signal
  const blockSignal = useCallback(async (
    signalId: string, 
    reason: BlockReason, 
    notes?: string
  ) => {
    setIsResolving(true);
    setError(null);

    try {
      await signalService.blockSignal(signalId, {
        reason,
        notes
      });
      
      // Refresh to get next signal
      await refreshSignals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block signal');
      throw err;
    } finally {
      setIsResolving(false);
    }
  }, [refreshSignals, signalService]);

  // Escalate a signal
  const escalateSignal = useCallback(async (
    signalId: string, 
    reason: string, 
    toRole?: string
  ) => {
    setIsResolving(true);
    setError(null);

    try {
      await signalService.escalateSignal(signalId, {
        escalation_reason: reason,
        escalate_to_role: toRole
      });
      
      // Refresh to get next signal
      await refreshSignals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to escalate signal');
      throw err;
    } finally {
      setIsResolving(false);
    }
  }, [refreshSignals, signalService]);

  // Request clarification
  const requestClarification = useCallback(async (
    signalId: string, 
    question: string
  ) => {
    setIsResolving(true);
    setError(null);

    try {
      await signalService.requestClarification(signalId, {
        question
      });
      
      // Refresh to get updated state
      await refreshSignals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request clarification');
      throw err;
    } finally {
      setIsResolving(false);
    }
  }, [refreshSignals, signalService]);

  // Acknowledge a signal (local state only)
  const acknowledgeSignal = useCallback((signalId: string) => {
    setCurrentSignal(prev => {
      if (prev?.id === signalId) {
        return { ...prev, status: 'acknowledged', acknowledged_at: new Date().toISOString() };
      }
      return prev;
    });
  }, []);

  // Initial load
  useEffect(() => {
    void refreshSignals();
  }, [refreshSignals]);

  // Auto-refresh if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      void refreshSignals();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refreshSignals]);

  return {
    currentSignal,
    pendingSignals,
    queueStatus,
    panelStatus,
    isLoading,
    isResolving,
    error,
    refreshSignals,
    completeSignal,
    blockSignal,
    escalateSignal,
    requestClarification,
    acknowledgeSignal
  };
}
